/**
 * TeamMembersSection - Gestão de membros da equipe
 */

import React, { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Badge } from '@/shared/components/ui/badge';
import { useTeamMembers } from '../../hooks/useTeamMembers';
import { useAddUserToTeam } from '../../hooks/useAddUserToTeam';
import { useRemoveUserFromTeam } from '../../hooks/useRemoveUserFromTeam';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { Icon } from '@/shared/icons'
import {
  DeleteConfirmationModal,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Select,
  type SelectOption,
} from '@/shared/components/ui';

interface TeamMembersSectionProps {
  teamId: string;
}

const ROLE_OPTIONS: SelectOption[] = [
  { value: 'member', label: 'Membro' },
  { value: 'leader', label: 'Líder' },
  { value: 'supervisor', label: 'Supervisor' },
];

export const TeamMembersSection: React.FC<TeamMembersSectionProps> = ({ teamId }) => {
  const { data: members = [], isLoading } = useTeamMembers(teamId);
  const { data: availableUsers = [] } = useUsersDropdown({ status: 'active' });
  const addMutation = useAddUserToTeam();
  const removeMutation = useRemoveUserFromTeam();

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<'member' | 'leader' | 'supervisor'>('member');
  const [memberToRemove, setMemberToRemove] = useState<{ userId: string; name: string } | null>(null);

  const handleAddMember = async () => {
    if (!selectedUserId) return;

    try {
      await addMutation.mutateAsync({
        teamId,
        userId: selectedUserId,
        role: selectedRole,
      });
      setShowAddModal(false);
      setSelectedUserId('');
    } catch (error) {
      console.error('Erro ao adicionar membro:', error);
    }
  };

  const handleConfirmRemove = async () => {
    if (!memberToRemove) return;

    try {
      await removeMutation.mutateAsync({ teamId, userId: memberToRemove.userId });
      setMemberToRemove(null);
    } catch (error) {
      console.error('Erro ao remover membro:', error);
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const roleLabels = {
    member: 'Membro',
    leader: 'Líder',
    supervisor: 'Supervisor',
  };

  // Cor da pill por papel (rolepill do mockup): líder=lilás, supervisor=azul, membro=neutro
  const roleBadgeVariant: Record<string, 'default' | 'info' | 'secondary'> = {
    leader: 'default',
    supervisor: 'info',
    member: 'secondary',
  };

  // Filter out users that are already members
  const memberIds = members.map((m: any) => m.id);
  const availableUsersFiltered = availableUsers.filter(u => !memberIds.includes(u.id));

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Section Header (.card-h) */}
      <div
        className="flex items-center justify-between gap-3 px-[18px] py-[15px] border-b"
        style={{ borderColor: 'var(--theme-card-border)' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: 'var(--theme-primary-background)',
              color: 'var(--theme-primary)',
            }}
          >
            <Icon name="users" className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-[15px] font-bold" style={{ color: 'var(--theme-card-text)' }}>
              Operadores da Equipe
            </h2>
            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              Membros da equipe e seus papéis
            </p>
          </div>
        </div>
        <Button onClick={() => setShowAddModal(true)} size="sm" variant="secondary" data-testid="team-add-member">
          <Icon name="user-plus" className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Adicionar Membro</span>
        </Button>
      </div>

      {/* Members list (.card-b) */}
      <div className="p-[18px]">
        {isLoading ? (
          <p className="text-sm" style={{ color: 'var(--theme-card-muted)' }}>Carregando...</p>
        ) : members.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--theme-card-muted)' }}>Nenhum membro atribuído</p>
        ) : (
          <div>
            {members.map((member: any, index: number) => (
              <div
                key={member.id}
                className={`flex items-center gap-3 py-3${index > 0 ? ' border-t' : ''}`}
                style={index > 0 ? { borderColor: 'var(--theme-card-border)' } : {}}
              >
                <Avatar className="h-9 w-9" isSquircle>
                  <AvatarImage src={member.avatar} isSquircle />
                  <AvatarFallback isSquircle>{getInitials(member.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm leading-tight truncate" style={{ color: 'var(--theme-card-text)' }}>
                    {member.name || member.email || 'Usuário sem nome'}
                  </p>
                  {member.email && (
                    <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                      {member.email}
                    </p>
                  )}
                </div>
                <Badge variant={roleBadgeVariant[member.role as string] || 'secondary'} className="text-xs">
                  {roleLabels[member.role as keyof typeof roleLabels] || 'Membro'}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMemberToRemove({ userId: member.userId, name: member.name || member.email || 'este membro' })}
                  disabled={removeMutation.isPending}
                >
                  <Icon name="trash" className="w-4 h-4" style={{ color: 'var(--theme-error-color)' }} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Member Modal (shared, claro-fixo) */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} maxWidth="md">
        <ModalHeader onClose={() => setShowAddModal(false)}>Adicionar Membro</ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
                Usuário
              </label>
              <Select
                value={selectedUserId}
                onChange={setSelectedUserId}
                placeholder="Selecione um usuário"
                options={availableUsersFiltered.map((u) => ({ value: u.id, label: u.name }))}
                data-testid="team-member-user"
              />
              <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                Apenas usuários ativos que ainda não fazem parte da equipe
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
                Função
              </label>
              <Select
                value={selectedRole}
                onChange={(v) => setSelectedRole(v as 'member' | 'leader' | 'supervisor')}
                options={ROLE_OPTIONS}
                data-testid="team-member-role"
              />
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button onClick={() => setShowAddModal(false)} variant="secondary" data-testid="team-member-cancel">
            Cancelar
          </Button>
          <Button
            onClick={handleAddMember}
            disabled={!selectedUserId || addMutation.isPending}
            data-testid="team-member-confirm"
          >
            Adicionar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Remove Member Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={memberToRemove !== null}
        onClose={() => setMemberToRemove(null)}
        onConfirm={handleConfirmRemove}
        title="Remover membro"
        message={`Tem certeza que deseja remover ${memberToRemove?.name ?? ''} da equipe?`}
        confirmButtonText="Remover"
        isLoading={removeMutation.isPending}
        loadingText="Removendo..."
      />
    </div>
  );
};

TeamMembersSection.displayName = 'TeamMembersSection';
